import Image, { AspectRatio } from "../Image"
import { Spacer, SpacerVariant, spacerOptionsAll } from "../components/layout"
import { Text, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../RedesignTypography"

import { Color } from "../components/typography"
import React from "react"
import { useLocalesState } from "../../shared/context/locales"
import { useTranslation } from "react-i18next"

interface ServiceProps {
    spacerTop?: SpacerVariant
    spacerBottom?: SpacerVariant
    headline: TypographyElement[]
    text: TypographyElement[]
    showEmail?: boolean
}

const Service = ({ spacerTop, spacerBottom, showEmail }: ServiceProps) => {
    const { t } = useTranslation()
    const { region } = useLocalesState()

    const contact = [
        {
            id: "phone",
            icon: <i className="lavita-icon text-lg leading-none sm:text-xl"></i>,
            text: t("infobox_phone_number"),
            href: `tel:${t("infobox_phone_url")}`,
        },
        region !== "TR" &&
            !!showEmail && {
                id: "mail",
                icon: <i className="lavita-icon text-lg leading-none sm:text-xl"></i>,
                text: t("service_mail"),
                href: `mailto:${t("service_mail")}`,
            },
    ].filter((item): item is Exclude<typeof item, false> => Boolean(item))

    return (
        <div className="mx-auto w-full px-6.25 xs:px-10 sm:px-15 md:w-fit">
            <Spacer variant={spacerTop} />
            <div className="grid w-full rounded-xl bg-background-tide px-5 py-7 xs:px-7 sm:rounded-2xl sm:px-20 sm:py-10 md:grid-cols-[1fr_auto] md:gap-5 md:py-16 md:pr-10 md:pl-16 lg:gap-3 lg:rounded-3xl lg:px-20 xl:gap-8">
                <Image
                    maxWidth={287}
                    aspectRatio={AspectRatio["1/1"]}
                    className="order-1 mb-5 w-[140px] shrink-0 justify-self-center rounded-full sm:mb-7 sm:w-[180px] md:order-2 md:mb-0 md:w-[207px] lg:w-[287px] lg:place-self-center"
                />
                <div className="order-2 md:order-1 md:w-[388px] lg:w-[475px]">
                    <Color>
                        <div className="text-center md:text-left">
                            <Typography propName="headline" allowList={["S4"]} />
                            <Typography
                                propName="text"
                                size="P2"
                                allowList={["link", "bold", "italic", "small", "sup", "ul", "cl"]}
                            />
                        </div>
                    </Color>
                    <div className="mt-7 grid gap-3 sm:gap-3.5">
                        {contact.map(item => (
                            <div
                                className="flex h-15 w-full items-center justify-center gap-1.5 justify-self-center rounded-full bg-white text-blue sm:h-[72px] sm:w-[327px] sm:gap-2 sm:px-10 md:justify-self-start"
                                key={item.id}
                            >
                                {item.icon}
                                <a className="text-md font-semibold hover:underline sm:text-lg" href={item.href}>
                                    {item.text}
                                </a>
                            </div>
                        ))}
                    </div>
                    <Text
                        propName="smallprint"
                        renderBlock={({ children }) => (
                            <p className="mt-3.5 text-center text-sm text-[#666] sm:mt-5 sm:text-sm/[22px] md:text-left lg:text-base">
                                {children}
                            </p>
                        )}
                        placeholder="Lorem ipsum dolor sit amet, consectetur adipiscing elit"
                    />
                </div>
            </div>
            <Spacer variant={spacerBottom} />
        </div>
    )
}

Service.schema = {
    name: "Service",
    label: "Service",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/Service.png`,
    sideEditProps: [
        {
            name: "showEmail",
            label: "Show Email",
            type: types.SideEditPropType.Boolean,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
    ],
    getDefaultProps: (): ServiceProps => ({
        headline: [
            {
                type: "h2",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet",
                        S4: true,
                    },
                ],
            },
        ],
        text: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
                    },
                ],
            },
        ],
        showEmail: true,
        spacerTop: SpacerVariant.XL,
        spacerBottom: SpacerVariant.XL,
    }),
}

export default Service
